import { AnimatePresence } from 'framer-motion';
import MobileMenuDirectionAnimation from './MobileMenuDirectionAnimation';
import MobileMenuLink from './MobileMenuLink';
import {
  NavigationByIdsFragment,
  NavigationFragment,
  NavigationLinkFragment,
  NavigationSublinkFragment,
} from '@business/gql/graphql';
import MobileSubMenuLink from './MobileSubMenuLink';
import MobileMenuPageLink from './MobileMenuPageLink';
import MobileMenuNavigationLink from './MobileMenuNavigationLink';
import { Maybe } from 'graphql/jsutils/Maybe';

type MobileMenuLinksProps = {
  navigationDirection: 'left' | 'right';
  remainingNavigation?: Array<NavigationFragment | null>;
  mainNavigation?: Array<NavigationLinkFragment | null>;
  campaignElement?: NavigationFragment | null;
  newsElement?: NavigationFragment | null;
  sublinksNavigation?: NavigationSublinkFragment | null;
  navigationLinkCollection?: Maybe<NavigationByIdsFragment>[];
  onClose: () => void;
  locale: string;
  onClick?: (id: string) => Promise<void>;
  onCampaignNavigationClick?: (id: string) => Promise<void>;
  menuLevel: number;
  currentNavigationLevel?: 'campaign';
};

export default function MobileMenuLinks({
  navigationDirection,
  remainingNavigation,
  campaignElement,
  newsElement,
  mainNavigation,
  sublinksNavigation,
  onClose,
  locale,
  onClick,
  onCampaignNavigationClick,
  menuLevel,
  navigationLinkCollection,
  currentNavigationLevel,
}: MobileMenuLinksProps) {
  return (
    <div className="flex flex-col border-t border-borderColor pb-3 pt-2">
      <AnimatePresence
        initial={false}
        mode="popLayout"
        custom={navigationDirection}>
        {menuLevel === 0 && !navigationLinkCollection && (
          <MobileMenuDirectionAnimation
            navigationDirection="left"
            key="0-level">
            <>
              {mainNavigation?.length &&
                mainNavigation?.map((link, idx) => (
                  <MobileMenuLink
                    key={`${link?.title}:${idx}:${link?.sys?.id}`}
                    link={link ?? undefined}
                    onClose={onClose}
                    locale={locale}
                    onClick={onClick as (id: string) => Promise<void>}
                    className={idx === 0 ? 'pt-0' : ''}
                  />
                ))}
              {campaignElement && (
                <MobileMenuNavigationLink
                  slug={campaignElement?.page?.slug ?? ''}
                  title={campaignElement?.title ?? ''}
                  name={campaignElement?.name ?? ''}
                  onClose={onClose}
                  onClick={onCampaignNavigationClick}
                  showArrow
                />
              )}
              {newsElement && (
                <MobileMenuPageLink
                  key={newsElement?.title}
                  link={newsElement ?? undefined}
                  onClose={onClose}
                  locale={locale}
                />
              )}
              {remainingNavigation?.length &&
                remainingNavigation?.map((link) => (
                  <MobileMenuPageLink
                    key={link?.title}
                    link={link ?? undefined}
                    onClose={onClose}
                    locale={locale}
                  />
                ))}
            </>
          </MobileMenuDirectionAnimation>
        )}
        {navigationLinkCollection && currentNavigationLevel === 'campaign' && (
          <MobileMenuDirectionAnimation
            navigationDirection={navigationDirection}
            key="1-level-navigationLinkCollection"
            menuLevel={menuLevel}>
            {navigationLinkCollection?.map((link) => {
              return (
                link?.internalLink?.__typename === 'Page' && (
                  <MobileMenuNavigationLink
                    key={link?.internalLink?.slug}
                    slug={link?.internalLink?.slug ?? ''}
                    title={link?.internalLink?.title ?? ''}
                    openInNewTab={link?.openInNewTab}
                    onClose={onClose}
                  />
                )
              );
            })}
          </MobileMenuDirectionAnimation>
        )}
        {menuLevel === 1 && (
          <MobileMenuDirectionAnimation
            navigationDirection={navigationDirection}
            key="1-level"
            menuLevel={menuLevel}>
            {sublinksNavigation &&
              menuLevel === 1 &&
              sublinksNavigation?.sublinksCollection?.items.map((link, idx) => (
                <MobileSubMenuLink
                  key={`${link?.title}-${link?.sys?.id}:${idx}`}
                  link={link ?? undefined}
                  onClose={onClose}
                  locale={locale}
                  onClick={onClick as (id: string) => Promise<void>}
                />
              ))}
          </MobileMenuDirectionAnimation>
        )}
        {menuLevel === 2 && (
          <MobileMenuDirectionAnimation
            navigationDirection="right"
            key="2-level">
            {sublinksNavigation &&
              sublinksNavigation?.sublinksCollection?.items.map((link, idx) => (
                <MobileSubMenuLink
                  key={`${link?.title}-${link?.sys?.id}:${idx}`}
                  link={link ?? undefined}
                  onClose={onClose}
                  locale={locale}
                />
              ))}
          </MobileMenuDirectionAnimation>
        )}
      </AnimatePresence>
    </div>
  );
}
